const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL;
if (!AUTH_API_URL) {
    throw new Error('REACT_APP_AUTH_API_URL is required at build time');
}

export interface LoginRequest {
    username: string;
    password: string;
}

export interface AuthTokens {
    access: string;
    refresh: string;
    user: {
        uuid: string;
        username: string;
        email: string;
        is_staff: boolean;
        is_active: boolean;
        date_joined: string;
    };
}

export interface SignupRequest {
    username: string;
    email: string;
    password1: string;
    password2: string;
    terms_of_service: boolean;
}

export interface SignupResponse {
    access?: string;
    refresh?: string;
    user?: {
        uuid: string;
        username: string;
        email: string;
    };
    error?: string;
}

export interface AuthError {
    error: string;
}

export const loginWithLambda = async (credentials: LoginRequest): Promise<AuthTokens | AuthError> => {
    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ request_type: 'login', ...credentials }),
        });
        const data = await response.json();
        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }
        return data as AuthTokens;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to login' };
    }
};

export const signupWithLambda = async (signupData: SignupRequest): Promise<SignupResponse | AuthError> => {
    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ request_type: 'signup', ...signupData }),
        });
        const data = await response.json();
        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }
        return data as SignupResponse;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to signup' };
    }
};

export const refreshTokenWithLambda = async (refreshToken: string): Promise<{ access: string; refresh?: string } | AuthError> => {
    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ request_type: 'refresh_token', refresh: refreshToken }),
        });
        const data = await response.json();
        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }
        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to refresh token' };
    }
};

export const requestPasswordResetWithLambda = async (username: string): Promise<{ status: string; message: string } | AuthError> => {
    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ request_type: 'request_password_reset', username }),
        });
        const data = await response.json();
        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }
        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to request password reset' };
    }
};

export const resetPasswordWithLambda = async (resetToken: string, newPassword: string): Promise<{ status: string; message: string } | AuthError> => {
    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ request_type: 'reset_password', reset_token: resetToken, new_password: newPassword }),
        });
        const data = await response.json();
        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }
        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to reset password' };
    }
};
